import { Component, computed, inject, input, output } from '@angular/core';
import type { ModalAction } from '@purolator/ui-kit-angular';
import { ModalComponent, TypographyComponent } from '@purolator/ui-kit-angular';
import { TranslationService } from '../../../../core/i18n';
import { formatShipmentCreationDate } from '../../utils/format-get-labels-dates';

/**
 * Confirmation dialog before cancelling a completed shipment from Get Labels (OM-69).
 * Figma popup pattern with PIN + creation date; no refund copy (OM-69 business rule).
 * OM-890: when an associated pickup exists, uses bodyWithPickup (ESN parity).
 */
@Component({
  selector: 'app-get-labels-cancel-shipment-dialog',
  imports: [ModalComponent, TypographyComponent],
  templateUrl: './get-labels-cancel-shipment-dialog.html',
  styleUrl: './get-labels-cancel-shipment-dialog.scss',
})
export class GetLabelsCancelShipmentDialog {
  private readonly translation = inject(TranslationService);

  readonly open = input(false);
  readonly submitting = input(false);
  /** Tracking PIN when known (Get Labels session). Omitted on email deep-link confirm. */
  readonly trackingPin = input('');
  /** YYYY-MM-DD shipment creation date when known. */
  readonly shipmentCreationDate = input('');
  /**
   * True when the shipment has a scheduled courier pickup (OM-890).
   * Selects bodyWithPickup vs generic body, matching ESN pickup-selected messaging.
   */
  readonly hasAssociatedPickup = input(false);
  readonly dismissed = output<void>();
  readonly confirmed = output<void>();

  protected readonly modalTitle = computed(() => {
    this.translation.revision();
    return this.translation.translate('createShipment.getLabels.cancelShipment.title');
  });

  protected readonly bodyText = computed(() => {
    this.translation.revision();
    const pin = this.trackingPin().trim();
    const creationDate = this.shipmentCreationDate().trim();
    if (!pin || !creationDate) {
      return this.translation.translate('createShipment.getLabels.cancelShipment.bodyFallback');
    }

    const params = {
      trackingPin: pin,
      date: formatShipmentCreationDate(creationDate, this.translation.locale()),
    };
    const bodyKey = this.hasAssociatedPickup()
      ? 'createShipment.getLabels.cancelShipment.bodyWithPickup'
      : 'createShipment.getLabels.cancelShipment.body';
    return this.translation.translate(bodyKey, params);
  });

  protected readonly actions = computed<ModalAction[]>(() => {
    this.translation.revision();
    const busy = this.submitting();
    return [
      {
        label: this.translation.translate('createShipment.getLabels.cancelShipment.no'),
        variant: 'secondary',
        onClick: () => this.onDismiss(),
      },
      {
        label: this.translation.translate(
          busy
            ? 'createShipment.getLabels.cancelShipment.submitting'
            : 'createShipment.getLabels.cancelShipment.yes',
        ),
        variant: 'primary',
        onClick: () => this.onConfirm(),
      },
    ];
  });

  /** Backdrop dismiss is disabled while the cancel request is in flight. */
  protected readonly closeOnBackdrop = computed(() => !this.submitting());

  /**
   * Closes the dialog without submitting cancel.
   */
  protected onDismiss(): void {
    if (this.submitting()) {
      return;
    }
    this.dismissed.emit();
  }

  /**
   * Confirms cancellation so the page can call POST /shipments/cancel.
   */
  protected onConfirm(): void {
    if (this.submitting()) {
      return;
    }
    this.confirmed.emit();
  }
}
